import { useSearchParams } from 'react-router';

import { AssetDestination } from '~/components/send-all-assets-to/SendAllAssetsTo';
import { queryKeys } from '~/routes/query-params';

import {
  POSTHOG_TRACKING_EVENTS,
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '../constants';
import { usePostHog } from '../usePostHog';

export const usePosthogEvents = () => {
  const { captureTrackingEvent } = usePostHog();
  const [searchParams] = useSearchParams();

  const getEmailTrackingParams = () => {
    const utmSource = searchParams.get(queryKeys.utmSource);
    const utmCampaign = searchParams.get(queryKeys.utmCampaign);

    if (utmSource === POSTHOG_TRACKING_SOURCES.EMAIL) {
      return {
        source: POSTHOG_TRACKING_SOURCES.EMAIL as PosthogTrackingSource,
        utm_campaign: utmCampaign,
      };
    }

    return null;
  };

  const trackDashboardOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.DASHBOARD_OPEN,
      properties: {
        source,
      },
    });

  const trackChartOpen = (source: PosthogTrackingSource, type?: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.CHART_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackConnectionsTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.CONNECTIONS_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackEventsDrawerOpen = (source: PosthogTrackingSource, type: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.EVENTS_DRAWER_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackTliDrawerOpen = (source: PosthogTrackingSource, type: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.TLI_DRAWER_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackAoDrawerOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.AO_DRAWER_OPEN,
      properties: {
        source,
      },
    });

  const trackMarketSummaryOpen = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.MARKET_SUMMARY_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackEarningsInsightDrawerOpen = (
    source: PosthogTrackingSource,
    type: 'preview' | 'recap',
    otherProperties: Record<string, unknown> = {}
  ) => {
    const emailParams = getEmailTrackingParams();
    const finalSource = emailParams?.source ?? source;
    const finalOtherProperties = emailParams
      ? { ...otherProperties, utm_campaign: emailParams.utm_campaign }
      : otherProperties;

    return captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.EARNINGS_INSIGHT_DRAWER_OPEN,
      properties: {
        ...finalOtherProperties,
        source: finalSource,
        type,
      },
    });
  };

  const trackAlfredQuerySent = ({
    source,
    type,
    via,
    isAutoGenerated,
    value,
    conversationId,
    messageId,
  }: {
    source: PosthogTrackingSource;
    type: string;
    via?: PosthogTrackingSource;
    isAutoGenerated?: boolean;
    value?: string;
    conversationId?: string;
    messageId?: string;
  }) => {
    const properties = {
      source,
      type,
      ...(via && { via }),
      ...(isAutoGenerated !== undefined && {
        is_auto_generated: isAutoGenerated,
      }),
      ...(value && { value }),
      ...(conversationId && { conversation_id: conversationId }),
      ...(messageId && { message_id: messageId }),
    };

    return captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.QUERY_SENT,
      properties,
    });
  };

  const trackWatchlistOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.WATCHLIST_OPEN,
      properties: {
        source,
      },
    });

  const trackWatchlistCreate = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.WATCHLIST_CREATE,
      properties: {
        source,
      },
    });

  const trackSearchOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SEARCH_OPEN,
      properties: {
        source,
      },
    });

  const trackAssetOpen = (source: PosthogTrackingSource, entityTag: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ASSET_OPEN,
      properties: {
        source,
        entity: entityTag,
      },
    });

  const trackThemeOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.THEME_OPEN,
      properties: {
        source,
      },
    });

  const trackNewsOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.NEWS_OPEN,
      properties: {
        source,
      },
    });

  const trackInsightsOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHTS_OPEN,
      properties: {
        source,
      },
    });

  const trackScenarioOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCENARIO_OPEN,
      properties: {
        source,
      },
    });

  const trackScenarioRan = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCENARIO_RAN,
      properties: {
        source,
      },
    });

  const trackScreenerOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCREENER_OPEN,
      properties: {
        source,
      },
    });

  const trackStreamOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.STREAM_OPEN,
      properties: {
        source,
      },
    });

  const trackKgBrandDrawerOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.KG_BRAND_DRAWER_OPEN,
      properties: {
        source,
      },
    });

  const trackKgGeographyDrawerOpen = (
    source: PosthogTrackingSource,
    type: 'country' | 'region'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.KG_GEOGRAPHY_DRAWER_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackCatalystDrawerOpen = (
    source: PosthogTrackingSource,
    type: 'company' | 'market',
    otherProperties: Record<string, unknown> = {}
  ) => {
    const emailParams = getEmailTrackingParams();
    const finalSource = emailParams?.source ?? source;
    const finalOtherProperties = emailParams
      ? { ...otherProperties, utm_campaign: emailParams.utm_campaign }
      : otherProperties;

    const trackingPayload = {
      eventName: POSTHOG_TRACKING_EVENTS.CATALYST_DRAWER_OPEN,
      properties: {
        ...finalOtherProperties,
        source: finalSource,
        type,
      },
    };

    return captureTrackingEvent(trackingPayload);
  };

  const trackScreenerCreate = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCREENER_CREATE,
      properties: {
        source,
      },
    });

  const trackScreenerUpdate = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCREENER_UPDATE,
      properties: {
        source,
      },
    });

  const trackSendAllAssets = (
    source: PosthogTrackingSource,
    destination: AssetDestination
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SEND_ALL_ASSETS,
      properties: {
        source,
        destination,
      },
    });

  const trackNewsFilter = (
    source: PosthogTrackingSource,
    type: 'most_read' | 'watchlist'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.NEWS_FILTER_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackAssetOverviewTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ASSET_OVERVIEW_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackWatchlistTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.WATCHLIST_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackScreenerTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCREENER_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackStreamTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.STREAM_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackStreamFilterSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.STREAM_FILTER_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackDashboardWhatsNewTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.DASHBOARD_WHATS_NEW_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackInsightsDrawerOpen = (
    source: PosthogTrackingSource,
    type: 'simple' | 'full',
    otherProperties: Record<string, unknown> = {}
  ) => {
    const emailParams = getEmailTrackingParams();
    const finalSource = emailParams?.source ?? source;
    const finalOtherProperties = emailParams
      ? { ...otherProperties, utm_campaign: emailParams.utm_campaign }
      : otherProperties;

    const trackingPayload = {
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHTS_DRAWER_OPEN,
      properties: {
        ...finalOtherProperties,
        source: finalSource,
        type,
      },
    };

    return captureTrackingEvent(trackingPayload);
  };

  const trackThemePerformanceWidgetTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.THEME_PERFORMANCE_WIDGET_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredPythonView = (
    source: PosthogTrackingSource,
    type: 'expand' | 'copy'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PYTHON_VIEW,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredProcessView = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PROCESS_VIEW,
      properties: {
        source,
      },
    });

  const trackAlfredShare = (
    source: PosthogTrackingSource,
    type: 'create' | 'copy' | 'continue'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_SHARE,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredPersonalize = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PERSONALIZE,
      properties: {
        source,
      },
    });

  const trackAlfredChatSearchOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHATSEARCH_OPEN,
      properties: {
        source,
      },
    });

  const trackAlfredChartAction = (
    source: PosthogTrackingSource,
    type: 'download' | 'expand'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHART_ACTION,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredFlowOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_FLOW_OPEN,
      properties: {
        source,
      },
    });

  const trackAlfredPlanningView = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PLANNING_VIEW,
      properties: {
        source,
      },
    });

  const trackAlfredChatBookmark = (
    source: PosthogTrackingSource,
    type: 'bookmark' | 'unbookmark'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHAT_BOOKMARK,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredDeepSelected = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_DEEP_SELECTED,
      properties: {
        source,
      },
    });

  const trackAlfredWebSearchSelected = (
    source: PosthogTrackingSource,
    type: 'general' | 'trusted' | 'none'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_WEB_SEARCH_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredPreviousConversationOpen = (
    source: PosthogTrackingSource,
    type?: 'shared'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PREVIOUS_CONVERSATION_OPEN,
      properties: {
        source,
        ...(type && { type }),
      },
    });

  const trackAlfredTrustedSourceAdded = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_TRUSTED_SOURCE_ADDED,
      properties: {
        source,
      },
    });

  const trackAlfredTrustedSourceRemoved = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_TRUSTED_SOURCE_REMOVED,
      properties: {
        source,
      },
    });

  const trackAlfredSourceOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_SOURCE_OPEN,
      properties: {
        source,
      },
    });

  const trackAlfredChatCopy = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHAT_COPY,
      properties: {
        source,
      },
    });

  const trackAlfredFeedback = (
    source: PosthogTrackingSource,
    type: 'thumbs_up' | 'thumbs_down'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_FEEDBACK,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredConversationPrint = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_PRINT,
      properties: {
        source,
      },
    });

  const trackAlfredConversationRename = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_RENAME,
      properties: {
        source,
      },
    });

  const trackAlfredConversationStarted = (
    source: PosthogTrackingSource | null
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_STARTED,
      properties: { source },
    });

  const trackAlfredOpened = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_OPENED,
      properties: {
        source,
      },
    });

  const trackThemeOverviewDrawerOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.THEME_OVERVIEW_DRAWER_OPEN,
      properties: {
        source,
      },
    });

  const trackEventPageOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.EVENT_PAGE_OPEN,
      properties: {
        source,
      },
    });

  const trackInsightIconClicked = (
    source: PosthogTrackingSource,
    type:
      | 'earnings_recap'
      | 'earnings_preview'
      | 'company_catalyst'
      | 'market_catalyst'
      | 'scenario'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHT_ICON_CLICKED,
      properties: {
        source,
        type,
      },
    });

  return {
    trackAssetOpen,
    trackThemeOpen,
    trackWatchlistOpen,
    trackWatchlistCreate,
    trackSearchOpen,
    trackNewsOpen,
    trackInsightsOpen,
    trackScenarioOpen,
    trackScenarioRan,
    trackScreenerOpen,
    trackScreenerCreate,
    trackScreenerUpdate,
    trackSendAllAssets,
    trackNewsFilter,
    trackDashboardOpen,
    trackChartOpen,
    trackAssetOverviewTabSelected,
    trackConnectionsTabSelected,
    trackEventsDrawerOpen,
    trackTliDrawerOpen,
    trackAoDrawerOpen,
    trackMarketSummaryOpen,
    trackAlfredQuerySent,
    trackWatchlistTabSelected,
    trackInsightsDrawerOpen,
    trackEarningsInsightDrawerOpen,
    trackStreamOpen,
    trackKgBrandDrawerOpen,
    trackKgGeographyDrawerOpen,
    trackCatalystDrawerOpen,
    trackScreenerTabSelected,
    trackStreamTabSelected,
    trackStreamFilterSelected,
    trackDashboardWhatsNewTabSelected,
    trackThemePerformanceWidgetTabSelected,
    trackAlfredPythonView,
    trackAlfredProcessView,
    trackAlfredShare,
    trackAlfredPersonalize,
    trackAlfredChatSearchOpen,
    trackAlfredChartAction,
    trackAlfredFlowOpen,
    trackAlfredPlanningView,
    trackAlfredChatBookmark,
    trackAlfredDeepSelected,
    trackAlfredWebSearchSelected,
    trackAlfredPreviousConversationOpen,
    trackAlfredTrustedSourceAdded,
    trackAlfredTrustedSourceRemoved,
    trackAlfredSourceOpen,
    trackAlfredChatCopy,
    trackAlfredFeedback,
    trackAlfredConversationPrint,
    trackAlfredConversationRename,
    trackAlfredConversationStarted,
    trackAlfredOpened,
    trackThemeOverviewDrawerOpen,
    trackEventPageOpen,
    trackInsightIconClicked,
  };
};
